import { useEffect, useRef, useState } from "react";
import {
  getAccessCodeLogs,
  AccessCodeLog,
  AccessCodeLogSort,
  AccessCodeEmailStatus,
} from "../../../api/api-hook";
import CopyButton from "../CopyButton";

const BRAND_GREEN = "#023822";

const thStyle: React.CSSProperties = { padding: "0.7rem 0.75rem", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "#6c757d", whiteSpace: "nowrap", background: "#f8f9fa" };

const pillStyle: React.CSSProperties = { display: "inline-block", padding: "0.2rem 0.6rem", borderRadius: 20, fontSize: "0.72rem", fontWeight: 600, whiteSpace: "nowrap" };

const formatDateTime = (iso: string) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { month: "short", day: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

// Columns that sort, with the direction a first click uses
const SORTABLE: Partial<Record<AccessCodeLogSort, { label: string; firstDir: "asc" | "desc" }>> = {
  requestedAt: { label: "Requested",  firstDir: "desc" },
  firstName:   { label: "First Name", firstDir: "asc" },
  lastName:    { label: "Last Name",  firstDir: "asc" },
  email:       { label: "Email",      firstDir: "asc" },
};

const CODE_STATUS: Record<AccessCodeLog["codeStatus"], { label: string; bg: string; color: string; title: string }> = {
  active:   { label: "Active",   bg: "rgba(25,135,84,0.1)",  color: "#146c43", title: "The golfer can still log in with this code" },
  expired:  { label: "Expired",  bg: "rgba(108,117,125,0.12)", color: "#495057", title: "Codes last 24 hours" },
  replaced: { label: "Replaced", bg: "rgba(108,117,125,0.08)", color: "#6c757d", title: "The golfer asked for a newer code, so this one no longer works" },
};

const EMAIL_STATUS: Record<AccessCodeEmailStatus, { label: string; bg: string; color: string; title: string }> = {
  opened:    { label: "Opened",    bg: "rgba(25,135,84,0.1)",   color: "#146c43", title: "Delivered and opened" },
  delivered: { label: "Delivered", bg: "rgba(13,110,253,0.1)",  color: "#0a58ca", title: "Delivered; not opened yet (some mail apps don't report opens)" },
  sent:      { label: "Sent",      bg: "rgba(108,117,125,0.12)", color: "#495057", title: "Sent; delivery not confirmed yet" },
  pending:   { label: "Sending…",  bg: "rgba(255,193,7,0.15)",  color: "#997404", title: "Requested in the last 5 minutes; the email may still be on its way" },
  bounced:   { label: "Bounced",   bg: "rgba(220,53,69,0.1)",   color: "#b02a37", title: "The golfer's mail server rejected the email" },
  dropped:   { label: "Dropped",   bg: "rgba(220,53,69,0.1)",   color: "#b02a37", title: "HubSpot didn't send it (e.g. the address previously bounced or unsubscribed)" },
  not_found: { label: "Not sent",  bg: "rgba(220,53,69,0.1)",   color: "#b02a37", title: "No code email was sent to this golfer for this request" },
  unknown:   { label: "—",         bg: "transparent",           color: "#6c757d", title: "Email activity couldn't be loaded from HubSpot" },
};

const AccessCodeLogs = () => {
  const [logs,          setLogs]          = useState<AccessCodeLog[]>([]);
  const [total,         setTotal]         = useState(0);
  const [golfers,       setGolfers]       = useState(0);
  const [activeCodes,   setActiveCodes]   = useState(0);
  const [emailProblems, setEmailProblems] = useState(0);
  const [emailEvents,   setEmailEvents]   = useState(true);
  const [updatedAt,     setUpdatedAt]     = useState("");
  const [loading,       setLoading]       = useState(false);
  const [refreshing,    setRefreshing]    = useState(false);
  const [error,         setError]         = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState("");
  const [search,      setSearch]      = useState("");
  const [status,      setStatus]      = useState("");

  const [sort, setSort] = useState<AccessCodeLogSort>("requestedAt");
  const [dir,  setDir]  = useState<"asc" | "desc">("desc");

  const [pageSize,    setPageSize]    = useState<20 | 50 | 100>(20);
  const [currentPage, setCurrentPage] = useState(1);

  // Ignore responses from requests that a newer one has replaced
  const requestId = useRef(0);

  const fetchLogs = async (refresh = false) => {
    const id = ++requestId.current;
    setLoading(true);
    setRefreshing(refresh);
    setError(null);
    try {
      const resp = await getAccessCodeLogs({ page: currentPage, pageSize, sort, dir, status, q: search, refresh });
      if (id !== requestId.current) return;
      if (resp.success) {
        setLogs(resp.logs);
        setTotal(resp.total);
        setGolfers(resp.golfers);
        setActiveCodes(resp.activeCodes);
        setEmailProblems(resp.emailProblems);
        setEmailEvents(resp.emailEvents);
        setUpdatedAt(resp.updatedAt);
      } else {
        setError(resp.message || "Failed to load access code logs.");
        setLogs([]);
      }
    } catch (err: any) {
      if (id !== requestId.current) return;
      setError(err?.response?.data?.message || "Network error. Please try again.");
      setLogs([]);
    } finally {
      if (id === requestId.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  };

  // Wait for typing to pause before searching
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => { setCurrentPage(1); }, [search, status, pageSize, sort, dir]);

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, sort, dir, status, search]);

  const sortBy = (key: AccessCodeLogSort) => {
    if (sort === key) {
      setDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSort(key);
      setDir(SORTABLE[key]!.firstDir);
    }
  };

  const sortHeader = (key: AccessCodeLogSort) => {
    const active = sort === key;
    return (
      <th
        style={{ ...thStyle, cursor: "pointer", userSelect: "none", color: active ? BRAND_GREEN : thStyle.color }}
        onClick={() => sortBy(key)}
        aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
      >
        {SORTABLE[key]!.label}
        <i
          className={`bi ${active ? (dir === "asc" ? "bi-caret-up-fill" : "bi-caret-down-fill") : "bi-chevron-expand"} ms-1`}
          style={{ opacity: active ? 1 : 0.4, fontSize: "0.7rem" }}
        />
      </th>
    );
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage   = Math.min(currentPage, totalPages);
  const firstShown = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const lastShown  = (safePage - 1) * pageSize + logs.length;

  return (
    <div>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
        <div>
          <h4 className="mb-1 fw-bold" style={{ color: BRAND_GREEN, letterSpacing: "-0.3px" }}>Access Code Log</h4>
          <p className="mb-0" style={{ fontSize: "0.82rem", color: "#8a9199" }}>
            <span style={{ fontWeight: 600, color: "#495057" }}>{total.toLocaleString()}</span> code request{total !== 1 ? "s" : ""}{" · "}
            <span style={{ fontWeight: 600, color: "#495057" }}>{golfers.toLocaleString()}</span> golfer{golfers !== 1 ? "s" : ""}{" · "}
            <span style={{ fontWeight: 600, color: "#146c43" }}>{activeCodes.toLocaleString()}</span> active{" · "}
            <span style={{ fontWeight: 600, color: emailProblems ? "#b02a37" : "#495057" }}>{emailProblems.toLocaleString()}</span> email problem{emailProblems !== 1 ? "s" : ""}
            {updatedAt && <>{" · "}updated {formatDateTime(updatedAt)}</>}
          </p>
        </div>
        <button
          onClick={() => fetchLogs(true)}
          disabled={loading}
          title="Reload every code request and email from HubSpot"
          style={{ backgroundColor: BRAND_GREEN, color: "#fff", border: "none", borderRadius: 8, padding: "0.35rem 1rem", fontSize: "0.82rem", fontWeight: 500 }}
        >
          <i className="bi bi-arrow-clockwise me-1" />{refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {/* Filters row */}
      <div style={{ background: "#f8f9fa", borderRadius: 12, padding: "0.85rem 1rem", marginBottom: "1.25rem", border: "1px solid #e9ecef" }}>
        <div className="d-flex align-items-end gap-2 flex-wrap">
          <div style={{ flex: "1 1 240px", maxWidth: 380 }}>
            <label className="form-label small text-muted mb-1" htmlFor="code-log-search">Search</label>
            <input
              id="code-log-search"
              type="text"
              className="form-control form-control-sm"
              placeholder="Name, email, VIP card # or code…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{ borderRadius: 8 }}
            />
          </div>
          <div>
            <label className="form-label small text-muted mb-1" htmlFor="code-log-status">Show</label>
            <select
              id="code-log-status"
              className="form-select form-select-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              style={{ borderRadius: 8, minWidth: 150 }}
            >
              <option value="">All requests</option>
              <option value="active">Active codes</option>
              <option value="problem">Email problems</option>
            </select>
          </div>
        </div>
      </div>

      {!emailEvents && !error && (
        <div className="alert alert-warning py-2 small">Email activity couldn't be loaded from HubSpot, so the Email column is blank. Try Refresh.</div>
      )}
      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      {/* Table */}
      {!error && (
        <div style={{ borderRadius: 12, border: "1px solid #e9ecef", overflow: "hidden", boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }}>
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0" style={{ fontSize: "0.84rem" }}>
            <thead>
              <tr style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #e9ecef" }}>
                <th style={{ ...thStyle, width: 36, paddingLeft: "0.75rem", paddingRight: 0 }} />
                {sortHeader("requestedAt")}
                {sortHeader("firstName")}
                {sortHeader("lastName")}
                {sortHeader("email")}
                <th style={thStyle}>VIP Card #</th>
                <th style={thStyle}>Code</th>
                <th style={thStyle}>Code Status</th>
                <th style={thStyle}>Email</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={9} className="text-center py-4 text-muted">
                    <div className="spinner-border spinner-border-sm me-2" role="status" />
                    {refreshing ? "Reloading code requests and emails from HubSpot…" : "Loading access code log…"}
                  </td>
                </tr>
              )}
              {!loading && logs.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center py-4 text-muted">
                    {search || status ? "No code requests match these filters." : "No golfer has asked for an access code yet."}
                  </td>
                </tr>
              )}
              {!loading &&
                logs.map((l) => {
                  const cs = CODE_STATUS[l.codeStatus];
                  const es = EMAIL_STATUS[l.emailStatus];
                  const emailTitle = [
                    es.title,
                    l.emailsSent > 1 && `Emailed ${l.emailsSent} times (the golfer asked again while this code was valid)`,
                    l.emailSentAt && `${l.emailsSent > 1 ? "Last sent" : "Sent"} ${formatDateTime(l.emailSentAt)}`,
                    l.emailOpenedAt && `Opened ${formatDateTime(l.emailOpenedAt)}`,
                  ].filter(Boolean).join("\n");
                  const sentElsewhere = l.sentTo && l.sentTo.toLowerCase() !== l.email.toLowerCase();
                  return (
                    <tr key={l.id}>
                      <td className="text-center px-0" style={{ paddingLeft: "0.75rem" }}>
                        <button
                          type="button"
                          className="btn btn-sm p-0 border-0"
                          title="View this golfer's offers page (opens a preview in a new tab)"
                          aria-label={`View offers as ${`${l.firstName} ${l.lastName}`.trim() || "this golfer"}`}
                          style={{ color: BRAND_GREEN, fontSize: "1rem", lineHeight: 1 }}
                          onClick={() => window.open(`/offers?previewContact=${encodeURIComponent(l.contactId)}`, "_blank")}
                        >
                          <i className="bi bi-eye" />
                        </button>
                      </td>
                      <td className="text-nowrap" title={l.source ? `Requested from: ${l.source}` : undefined}>{formatDateTime(l.requestedAt)}</td>
                      <td>{l.firstName || "—"}</td>
                      <td>{l.lastName || "—"}</td>
                      <td>
                        {l.email ? (
                          <span className="text-nowrap">
                            <a href={`mailto:${l.email}`} className="text-decoration-none" style={{ color: BRAND_GREEN }}>{l.email}</a>
                            <CopyButton text={l.email} label="email" />
                          </span>
                        ) : "—"}
                        {sentElsewhere && (
                          <div className="text-muted" style={{ fontSize: "0.72rem" }} title="The contact's email was different when this code was sent">
                            sent to {l.sentTo}
                          </div>
                        )}
                      </td>
                      <td><span style={{ fontFamily: "monospace", fontSize: "0.8rem" }}>{l.vipCardNumber || "—"}</span></td>
                      <td>
                        <span
                          style={{ fontFamily: "monospace", fontSize: "0.85rem", letterSpacing: "0.08em", color: l.codeStatus === "active" ? "#212529" : "#8a9199", textDecoration: l.codeStatus === "replaced" ? "line-through" : "none" }}
                        >
                          {l.code}
                        </span>
                      </td>
                      <td>
                        <span style={{ ...pillStyle, backgroundColor: cs.bg, color: cs.color }} title={cs.title}>{cs.label}</span>
                        {l.codeStatus === "active" && (
                          <div className="text-muted text-nowrap" style={{ fontSize: "0.72rem", marginTop: 2 }}>until {formatDateTime(l.validUntil)}</div>
                        )}
                      </td>
                      <td>
                        <span style={{ ...pillStyle, backgroundColor: es.bg, color: es.color }} title={emailTitle}>{es.label}</span>
                        {l.emailsSent > 1 && <span className="ms-1 small text-muted" title={emailTitle}>×{l.emailsSent}</span>}
                        {l.emailSentAt && (
                          <div className="text-muted text-nowrap" style={{ fontSize: "0.72rem", marginTop: 2 }}>
                            {l.emailOpenedAt ? `opened ${formatDateTime(l.emailOpenedAt)}` : `sent ${formatDateTime(l.emailSentAt)}`}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
        </div>
      )}

      {/* Pagination */}
      {!error && total > 0 && (
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-3">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="small text-muted">Rows per page:</span>
            {([20, 50, 100] as const).map((n) => (
              <button key={n} type="button" className="btn btn-sm"
                style={{ minWidth: 40, backgroundColor: pageSize === n ? BRAND_GREEN : "transparent", color: pageSize === n ? "#fff" : "#495057", border: `1px solid ${pageSize === n ? BRAND_GREEN : "#dee2e6"}`, fontWeight: pageSize === n ? 600 : 400 }}
                onClick={() => setPageSize(n)}>{n}</button>
            ))}
            <span className="small text-muted ms-2">
              {firstShown.toLocaleString()}–{lastShown.toLocaleString()} of {total.toLocaleString()}
            </span>
          </div>
          <div className="d-flex align-items-center gap-1">
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={loading || safePage === 1} onClick={() => setCurrentPage(1)}>«</button>
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={loading || safePage === 1} onClick={() => setCurrentPage((p) => p - 1)}>‹</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - safePage) <= 2)
              .reduce<(number | "…")[]>((acc, p, i, arr) => { if (i > 0 && p - (arr[i - 1] as number) > 1) acc.push("…"); acc.push(p); return acc; }, [])
              .map((p, i) => p === "…"
                ? <span key={`e-${i}`} className="px-1 small text-muted">…</span>
                : <button key={p} type="button" className="btn btn-sm" disabled={loading}
                    style={{ minWidth: 34, backgroundColor: p === safePage ? BRAND_GREEN : "transparent", color: p === safePage ? "#fff" : "#495057", border: `1px solid ${p === safePage ? BRAND_GREEN : "#dee2e6"}`, fontWeight: p === safePage ? 600 : 400 }}
                    onClick={() => setCurrentPage(p as number)}>{p}</button>
              )}
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={loading || safePage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>›</button>
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={loading || safePage === totalPages} onClick={() => setCurrentPage(totalPages)}>»</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessCodeLogs;
